import useDownloadWithProgress from "@/hooks/useDownloadWithProgress";
import type {TInstagramContent} from "@/store/types";
import {getContentPublishedDate} from "@/utils/mediaUtils";
import dayjs from "dayjs";
import {useAsyncFn} from "react-use";

const useContentDownload = (contents: TInstagramContent[], namePrefix: string | null | undefined) => {
  const content = contents[0];
  const isSourceContent = content?.instagram_media?.media_product_type === "SOURCE";
  const {downloadUrl, downloadZip, progress} = useDownloadWithProgress();

  const getFileName = () => {
    const sharedFileNameElements =
      (namePrefix?.replaceAll(" ", "_") ?? "content") +
      "_" +
      dayjs(getContentPublishedDate({media: content.instagram_media})).format("DD.MM.YYYY") +
      "_" +
      (content.user?.username ??
        `${content.user?.first_name}${content.user?.last_name ? "_" + content.user.last_name : ""}`);
    if (content.instagram_media?.media_type === "CAROUSEL_ALBUM" || isSourceContent)
      return sharedFileNameElements.replaceAll("/", "_") + ".zip";
    if (content.instagram_media?.media_type === "VIDEO") {
      const videoMedia = content.instagram_media?.media_file!.split("/").pop();
      return sharedFileNameElements + "." + videoMedia?.split(".").at(-1)?.split("?")[0];
    }
    const contentName = content.image.split("/").pop();
    return sharedFileNameElements + "." + contentName?.split(".").at(-1)?.split("?")[0];
  };

  const [{loading: isDownloading}, downloadMedia] = useAsyncFn(async () => {
    if (isSourceContent || content.instagram_media?.media_type === "CAROUSEL_ALBUM") {
      const mediaToDownload =
        content.instagram_media?.media_type === "CAROUSEL_ALBUM"
          ? (content.instagram_media?.children ?? [])
          : contents.map(c => c.instagram_media);

      await downloadZip({
        files: mediaToDownload!.map((media, index) => {
          const name = media?.media_file?.split("/").pop()?.split("?")[0];
          return {
            name: getFileName().split(".zip")[0] + "_" + (index + 1) + "." + name!.split(".").at(-1),
            url: media?.media_file!,
          };
        }),
        filename: getFileName(),
        folderName: isSourceContent ? "sourceContent" : "carousel",
      });
      return;
    }

    if (content.instagram_media?.media_type === "IMAGE") {
      //content.image is the same as content.instagram_media.thumbnail_file
      await downloadUrl({filename: getFileName(), url: content.image});
    }

    if (content.instagram_media?.media_type === "VIDEO") {
      await downloadUrl({filename: getFileName(), url: content.instagram_media?.media_file!});
    }
  }, [content, downloadUrl, downloadZip, getFileName]);

  return {downloadMedia, isDownloading, progress};
};

export default useContentDownload;
